import { useMemo, useState } from 'react'
import { DataTableInfinite } from '@/components/data-table/data-table-infinite'
import { DataTableStoreProvider } from '@/lib/store/provider/DataTableStoreProvider'
import { useNuqsAdapter } from '@/lib/store/adapters/nuqs'
import { useFilterState } from '@/lib/store/hooks/useFilterState'
import { useFilterActions } from '@/lib/store/hooks/useFilterActions'
import { useSigningDocumentsQuery } from './queries'
import { filterDocuments, type CollectedDocument } from './model'
import {
  DOCUMENTS_TABLE_ID,
  documentsColumns,
  documentsFilterFields,
  documentsSchema,
} from './table'
import { SigningDocumentSheet } from './SigningDocumentSheet'

const noMorePages = async () => undefined
function DocumentsTable() {
  const query = useSigningDocumentsQuery()
  const filters = useFilterState<typeof documentsSchema._type>()
  const { resetAllFilters } = useFilterActions()
  const data = useMemo(
    () => filterDocuments(query.data ?? [], filters ?? {}),
    [query.data, filters]
  )
  const [selection] = useState(() =>
    typeof filters?.uuid === 'string' ? { [filters.uuid]: true } : {}
  )
  const [sorting] = useState(() => {
    const sort = filters.sort
    const valid = documentsColumns.some(
      (column) => 'accessorKey' in column && column.accessorKey === sort?.id
    )
    return valid && sort ? [sort] : [{ id: 'createdAt', desc: true }]
  })
  return (
    <DataTableInfinite<CollectedDocument, unknown>
      tableId={DOCUMENTS_TABLE_ID}
      data={data}
      columns={documentsColumns}
      filterFields={documentsFilterFields}
      manualFiltering
      defaultColumnSorting={sorting}
      defaultRowSelection={selection}
      getRowId={(row) => row.id}
      fetchNextPage={noMorePages}
      refetch={() => void query.refetch()}
      isLoading={query.isPending}
      isFetching={query.isFetching}
      isError={query.isError}
      error={query.error}
      hasNextPage={false}
      totalRows={data.length}
      totalRowsFetched={data.length}
      entityLabel="documents"
      onClearFilters={resetAllFilters}
      sheetSlot={<SigningDocumentSheet />}
      autoColumnWidth
      flexColumnId="configurationName"
    />
  )
}

export function SigningDocumentsPage() {
  const adapter = useNuqsAdapter(documentsSchema.definition, {
    id: DOCUMENTS_TABLE_ID,
    history: 'replace',
    namespaceUrlKeys: true,
  })
  return (
    <DataTableStoreProvider adapter={adapter}>
      <DocumentsTable />
    </DataTableStoreProvider>
  )
}
